import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Check, CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getResearchTypeById } from '@/types/research-types';
import { ColorVariant } from '@/lib/colors';

export interface StyleOption {
  id: string;
  name: string;
  icon?: React.ElementType;
}

export interface StylishSelectorProps {
  styles: StyleOption[];
  selectedStyle: string;
  onSelectStyle: (style: string) => void;
  className?: string;
  /**
   * Color variant for the component
   * @default 'blue'
   */
  colorVariant?: ColorVariant;
  /**
   * Color for the check icon
   * @default 'green-500'
   */
  checkColor?: string;
  /**
   * Custom color mapping for individual items
   * Maps style.id to a ColorVariant
   */
  itemColorMap?: Record<string, ColorVariant>;
}

export const StylishSelector: React.FC<StylishSelectorProps> = ({
  styles,
  selectedStyle,
  onSelectStyle,
  className = '',
  colorVariant = 'blue',
  checkColor = 'green-500',
  itemColorMap = {}
}) => {
  // Color mapping for borders and backgrounds
  const getItemColorClasses = (styleId: string) => {
    // Check if this is a research type and get its color variant
    const researchType = getResearchTypeById(styleId);
    const itemVariant = researchType?.colorVariant || itemColorMap[styleId] || colorVariant;
    
    // Define color classes based on selection state and color variant
    const colorClasses = {
      blue: {
        selected: "border-blue-300 bg-blue-50/30 shadow-sm dark:border-blue-800 dark:bg-blue-900/20",
        default: "border-blue-200/40 bg-transparent hover:border-blue-300/60 dark:border-blue-800/30 dark:hover:border-blue-700/50"
      },
      green: {
        selected: "border-green-300 bg-green-50/30 shadow-sm dark:border-green-800 dark:bg-green-900/20",
        default: "border-green-200/40 bg-transparent hover:border-green-300/60 dark:border-green-800/30 dark:hover:border-green-700/50"
      },
      purple: {
        selected: "border-purple-300 bg-purple-50/30 shadow-sm dark:border-purple-800 dark:bg-purple-900/20",
        default: "border-purple-200/40 bg-transparent hover:border-purple-300/60 dark:border-purple-800/30 dark:hover:border-purple-700/50"
      },
      amber: {
        selected: "border-amber-300 bg-amber-50/30 shadow-sm dark:border-amber-800 dark:bg-amber-900/20",
        default: "border-amber-200/40 bg-transparent hover:border-amber-300/60 dark:border-amber-800/30 dark:hover:border-amber-700/50"
      },
      red: {
        selected: "border-red-300 bg-red-50/30 shadow-sm dark:border-red-800 dark:bg-red-900/20",
        default: "border-red-200/40 bg-transparent hover:border-red-300/60 dark:border-red-800/30 dark:hover:border-red-700/50"
      },
      indigo: {
        selected: "border-indigo-300 bg-indigo-50/30 shadow-sm dark:border-indigo-800 dark:bg-indigo-900/20",
        default: "border-indigo-200/40 bg-transparent hover:border-indigo-300/60 dark:border-indigo-800/30 dark:hover:border-indigo-700/50"
      }
    };
    
    // Return the appropriate class based on selection state
    return colorClasses[itemVariant]?.[selectedStyle === styleId ? 'selected' : 'default'] || 
           colorClasses.blue[selectedStyle === styleId ? 'selected' : 'default'];
  };
  
  // Get the icon color class
  const getIconColorClass = (styleId: string) => {
    // Check if this is a research type and get its color variant
    const researchType = getResearchTypeById(styleId);
    const itemVariant = researchType?.colorVariant || itemColorMap[styleId] || colorVariant;
    
    // Define color classes for each variant
    const iconColorClasses = {
      blue: "text-blue-500 dark:text-blue-400",
      green: "text-green-500 dark:text-green-400",
      purple: "text-purple-500 dark:text-purple-400",
      amber: "text-amber-500 dark:text-amber-400",
      red: "text-red-500 dark:text-red-400",
      indigo: "text-indigo-500 dark:text-indigo-400"
    };
    
    // Return the appropriate class
    return iconColorClasses[itemVariant] || iconColorClasses.blue;
  };

  return (
    <div 
      className={cn(
        "grid grid-cols-2 gap-3 mt-3",
        className
      )}
    >
      {styles.map((style) => (
        <motion.button
          key={style.id}
          type="button"
          onClick={() => onSelectStyle(style.id)}
          className={cn(
            "relative flex flex-col items-center justify-center p-4 border rounded-lg",
            getItemColorClasses(style.id)
          )}
          initial={{ scale: 0.95, opacity: 0.8 }}
          animate={{ scale: 1, opacity: 1 }}
          whileHover={{ scale: 1.03, transition: { duration: 0.2 } }}
          whileTap={{ scale: 0.97 }}
          transition={{ type: 'spring', stiffness: 300, damping: 15 }}
          layout
        >
          <AnimatePresence>
            {selectedStyle === style.id && (
              <motion.div 
                className="absolute -top-2 -right-2"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 500, damping: 20 }}
              >
                <motion.div
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                >
                  <CheckCircle className={cn(
                    "h-5 w-5 fill-white dark:fill-gray-900",
                    checkColor === 'green-500' ? "text-green-500" :
                    checkColor === 'blue-500' ? "text-blue-500" :
                    checkColor === 'purple-500' ? "text-purple-500" :
                    checkColor === 'amber-500' ? "text-amber-500" :
                    checkColor === 'red-500' ? "text-red-500" :
                    checkColor === 'indigo-500' ? "text-indigo-500" :
                    "text-green-500"
                  )} />
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
          <motion.div 
            className={cn("mb-2", getIconColorClass(style.id))}
            animate={selectedStyle === style.id ? { scale: [1, 1.2, 1], y: [0, -2, 0] } : {}}
            transition={{ duration: 0.4 }}
          >
            {style.icon && (
              <style.icon className="h-6 w-6" />
            )}
          </motion.div>
          <motion.div 
            className="text-sm font-medium"
            animate={selectedStyle === style.id ? { fontWeight: 600 } : { fontWeight: 500 }}
            transition={{ duration: 0.3 }}
          >
            {style.name}
          </motion.div>
        </motion.button>
      ))}
    </div>
  );
};

export default StylishSelector;
